<template>
  <table class="table" v-if="dateArr">
    <tbody>
    <tr v-for="(user, userIndex) in users" @mouseover="hoverRow(userIndex)" :class="{hover: hover === userIndex}" :key="user.zupGuid">
      <template v-if="user.dateArr">
        <td v-for="(item, position) in user.dateArr"
            :key="item.key + user.id"
            :class="getCellClassNames(item.openCreateShift, dateArr[position], user)"
            @click="openShifts($event, [position, userIndex], item.openCreateShift)"
            data-qa="main-table-cell-open-shift"
           >
          <template v-if="user.timeShift">

            <div class="table__cell-body table__cell"
                 :data-count="(user.timeShift[item.key] && user.timeShift[item.key].length > 2) ? user.timeShift[item.key].length : null"
            >
              <!-- Окно создания смены -->
              <vita-shift-control
                v-if="item.openCreateShift && currentDate"
                :user="user"
                :date="item.key"
                :position="shiftModalPosition(position, user.dateArr.length, userIndex, users.length)"
                :show-create="!isVacationOnDay(user.timeShift, item)"
                :is-user-inactive="isInactive(dateArr[position], user.dateOfEmployment, user.dateDismissal)"
              />

              <!-- Отображаем превью первого кадрового мероприятия -->
              <template v-if="isVacationOnDay(user.timeShift, item) && user.timeShift[item.key].length > 0">
                <vita-vacation-type :vacation-obj="user.timeShift[item.key][0][0]" />
              </template>

              <!-- Превью смен -->
              <vita-time-slot
                :time="time"
                :key="`T-${time.id}`"
                v-for="(time, index) in user.timeShift[item.key]"
                :index="index"
                :shiftsLength="user.timeShift[item.key].length"
              />
              <!-- Превью уведомления о наличии отменённых смен -->
              <div
                class="has-canceled"
                v-if="isGurvRole && hasCanceledShifts(user.timeShift[item.key])"
                title="Есть отмененные смены"
              >
                Отмен. смены
              </div>
            </div>
          </template>
        </td>
      </template>
    </tr>
    </tbody>
  </table>
</template>

<script lang="ts" setup>
import {useStore} from "vuex";
import {computed, ref} from "vue";
import { DateKeys, NewUser, TimeRange } from '@/types';
import VitaShiftControl from "@/components/VitaShiftControl.vue";
import VitaVacationType from "@/components/VitaVacationType.vue";
import VitaTimeSlot from '@/components/VitaTimeSlot.vue';
import isBefore from "date-fns/isBefore";
import { addDays, isAfter, parse, startOfDay } from 'date-fns';

const store = useStore<Storage>()

const users = computed<NewUser[]>(() => {
  return store.getters['data/pharmUsers']
})

const hoverRow = (index: number) => {
  store.commit('hoverRow', index)
}

const hover = computed<null | number>(() => {
  return store.getters['hover']
})

const dateArr = computed<DateKeys[]>(() => {
  return store.getters['data/timeScheduleKeys']
})

const isGurvRole = computed<boolean>(() => {
  return store.getters['getGurvRole']
})

const currentDate = ref<Date>(new Date())

const shiftModalPosition = (dateIndex:number, dateLength:number, userIndex:number, userLength:number): string => {
  let topPosition = userLength - 4 > userIndex ? 'top' : 'bottom'
  if (userLength > 4 && (userIndex === 2 || userIndex === 3)) {
    topPosition = 'center'
  }
  // У всех сотрудников на первой и второй строчке графика модалка открывается вниз
  if (userIndex <= 1) {
    topPosition = 'top'
  }

  const leftPosition = dateLength - 6 > dateIndex && elementLeft.value ? 'left' : 'right'

  // Последним параметром добавлен CSS класс для вложенных всплывающих элементов (фикс выхода за пределы графика)
  const menuLeftPosition = dateLength === (dateIndex + 1) ? 'm-right' : ''
  return `${leftPosition} ${topPosition} ${menuLeftPosition}`
}

const elementLeft = ref<boolean>(false)
const openShifts = (event: MouseEvent, item:number[], open:boolean) => {
  if (open) return

  const targetElement = event.target as HTMLElement;
  elementLeft.value = targetElement.getBoundingClientRect().right - window.innerWidth <= -580
  store.commit('data/closeCreateShift')
  store.commit('data/openCreateShift', item)
}

// Проверка на КМ в выбранный день
const isVacationOnDay = (timeShift, item) => {
  return Boolean(timeShift?.[item.key]?.length)
}

// Получение классов для оформления ячейки в таблице в зависимости от статуса дня
const getCellClassNames = (item, dateArrItem, user) => {
  const classMap = {
    'active': item,
    'dayOut': dateArrItem.dayOut,
    'holiday': dateArrItem.holiday,
    'today': dateArrItem.today,
    "user-inactive": isInactive(dateArrItem, user.dateOfEmployment, user.dateDismissal)
  };

  // Фильтруем по условиям и получаем названия классов
  return Object.entries(classMap).filter((entry) => entry[1]).map(([className]) => className);
}

// Проверяем на наличие смен со статусом Canceled и MissAccepted (OMR-3385)
const hasCanceledShifts = (arr: TimeRange[]) => {
  if (!arr) return false
  return arr.some(shift => shift.status === "Canceled" || shift.status === "MissAccepted");
}

// Проверяем что дата не входит в период отсутствия трудоустройства
const isInactive = (date: DateKeys, dateOfEmployment?: string | null, dateDismissal?: string | null): boolean => {
  if (!date || !date.key) return false

  // Приводим даты к формату и устанавливаем начало дня у текущего (date.key)
  const parsedCellDate = startOfDay(parse(date.key, "dd.MM.yyyy", new Date()));
  const parsedDateDismissal = dateDismissal ? new Date(dateDismissal) : null;
  const parsedDateEmployment = dateOfEmployment ? new Date(dateOfEmployment) : null;

  // Если есть дата трудоустройства, то закрашиваем все ячейки до этой даты
  if (parsedDateEmployment) {
    const actualDateEmployment = addDays(parsedDateEmployment, -1)
    if (isBefore(parsedCellDate, actualDateEmployment)) return true
  }

  // Если есть дата увольнения, то закрашиваем все ячейки начиная с этой даты
  if (parsedDateDismissal && isAfter(parsedCellDate, parsedDateDismissal)) return true

  return false
}
</script>

<style lang="scss">
tr.hover {
  td.holiday:not(.active) .table__cell {
    background: #D6DAEE;
  }

  td.dayOut:not(.holiday):not(.active) .table__cell {
    background: #C8E7FF;
  }

  .table__cell {
    background: #D6EEFF;
  }
}

td {
  position: relative;
}

.table__cell-body .more {
  position: absolute;
  right: 4px;
  bottom: 4px;
  font-size: 12px;
  font-weight: 500;
  line-height: 15px;
  color: #30A8FF;
}

.time-slot-wrap {
  position: relative;

  & + .time-slot-wrap {
    margin-top: 12px;
  }
}

.time-slot {
  position: relative;
  z-index: 2;
}

.time-slot-inner:hover .time-slot-label {
  transform: translateY(-10px);
}

.time-slot-label {
  position: absolute;
  top: -3px;
  left: 0;
  right: 50%;
  background: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODQiIGhlaWdodD0iMjIiIHZpZXdCb3g9IjAgMCA4NCAyMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICAgIDxwYXRoIGQ9Ik0wLjUgMTguNVYzQzAuNSAxLjYxOTI5IDEuNjE5MjkgMC41IDMgMC41SDc4Ljc1NzRDNzkuNDIwNCAwLjUgODAuMDU2MyAwLjc2MzM5MiA4MC41MjUxIDEuMjMyMjNMODIuNzY3OCAzLjQ3NDg3QzgzLjIzNjYgMy45NDM3MiA4My41IDQuNTc5NiA4My41IDUuMjQyNjRWMTguNUM4My41IDE5Ljg4MDcgODIuMzgwNyAyMSA4MSAyMUgzQzEuNjE5MjkgMjEgMC41IDE5Ljg4MDcgMC41IDE4LjVaIiBmaWxsPSIjRTFGNUYxIi8+CiAgICA8cGF0aCBkPSJNMC41IDE4LjVWM0MwLjUgMS42MTkyOSAxLjYxOTI5IDAuNSAzIDAuNUg3OC43NTc0Qzc5LjQyMDQgMC41IDgwLjA1NjMgMC43NjMzOTIgODAuNTI1MSAxLjIzMjIzTDgyLjc2NzggMy40NzQ4N0M4My4yMzY2IDMuOTQzNzIgODMuNSA0LjU3OTYgODMuNSA1LjI0MjY0VjE4LjVDODMuNSAxOS44ODA3IDgyLjM4MDcgMjEgODEgMjFIM0MxLjYxOTI5IDIxIDAuNSAxOS44ODA3IDAuNSAxOC41WiIgZmlsbD0iIzY4Q0RCQiIvPgogICAgPHBhdGggZD0iTTAuNSAxOC41VjNDMC41IDEuNjE5MjkgMS42MTkyOSAwLjUgMyAwLjVINzguNzU3NEM3OS40MjA0IDAuNSA4MC4wNTYzIDAuNzYzMzkyIDgwLjUyNTEgMS4yMzIyM0w4Mi43Njc4IDMuNDc0ODdDODMuMjM2NiAzLjk0MzcyIDgzLjUgNC41Nzk2IDgzLjUgNS4yNDI2NFYxOC41QzgzLjUgMTkuODgwNyA4Mi4zODA3IDIxIDgxIDIxSDNDMS42MTkyOSAyMSAwLjUgMTkuODgwNyAwLjUgMTguNVoiIHN0cm9rZT0iIzY4Q0RCQiIvPgo8L3N2Zz4=");
  background-repeat: no-repeat;
  background-position: top right;
  -webkit-background-size: cover;
  background-size: cover;
  color: #FFFFFF;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 10px;
  line-height: 12px;
  text-align: left;
  padding: 1px 5px;
  padding-top: 2px;
  z-index: 1;
  border-radius: 3px 0 0 0;
  min-width: 42px;
  transition: all .15s;
  user-select: none;
  overflow: hidden;
}

.time-slot-big-inner .time-slot-label {
  min-width: 84px;
}

.time-slot-label-split {
  min-width: unset;

  & + .time-slot-label-split {
    left: 45%;
    right: 0;
  }
}

.exchange-style {
  background: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODQiIGhlaWdodD0iMjIiIHZpZXdCb3g9IjAgMCA4NCAyMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICAgIDxwYXRoIGQ9Ik0wLjUgMTguNVYzQzAuNSAxLjYxOTI5IDEuNjE5MjkgMC41IDMgMC41SDc4Ljc1NzRDNzkuNDIwNCAwLjUgODAuMDU2MyAwLjc2MzM5MiA4MC41MjUxIDEuMjMyMjNMODIuNzY3OCAzLjQ3NDg3QzgzLjIzNjYgMy45NDM3MiA4My41IDQuNTc5NiA4My41IDUuMjQyNjRWMTguNUM4My41IDE5Ljg4MDcgODIuMzgwNyAyMSA4MSAyMUgzQzEuNjE5MjkgMjEgMC41IDE5Ljg4MDcgMC41IDE4LjVaIiBmaWxsPSIjRDFFOUZGIi8+CiAgICA8cGF0aCBkPSJNMC41IDE4LjVWM0MwLjUgMS42MTkyOSAxLjYxOTI5IDAuNSAzIDAuNUg3OC43NTc0Qzc5LjQyMDQgMC41IDgwLjA1NjMgMC43NjMzOTIgODAuNTI1MSAxLjIzMjIzTDgyLjc2NzggMy40NzQ4N0M4My4yMzY2IDMuOTQzNzIgODMuNSA0LjU3OTYgODMuNSA1LjI0MjY0VjE4LjVDODMuNSAxOS44ODA3IDgyLjM4MDcgMjEgODEgMjFIM0MxLjYxOTI5IDIxIDAuNSAxOS44ODA3IDAuNSAxOC41WiIgZmlsbD0iIzE4OTFGRiIvPgogICAgPHBhdGggZD0iTTAuNSAxOC41VjNDMC41IDEuNjE5MjkgMS42MTkyOSAwLjUgMyAwLjVINzguNzU3NEM3OS40MjA0IDAuNSA4MC4wNTYzIDAuNzYzMzkyIDgwLjUyNTEgMS4yMzIyM0w4Mi43Njc4IDMuNDc0ODdDODMuMjM2NiAzLjk0MzcyIDgzLjUgNC41Nzk2IDgzLjUgNS4yNDI2NFYxOC41QzgzLjUgMTkuODgwNyA4Mi4zODA3IDIxIDgxIDIxSDNDMS42MTkyOSAyMSAwLjUgMTkuODgwNyAwLjUgMTguNVoiIHN0cm9rZT0iIzE4OTFGRiIvPgo8L3N2Zz4=");
  background-repeat: no-repeat;
  background-position: top right;
  -webkit-background-size: cover;
  background-size: cover;
}

.partner-style {
  background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODQiIGhlaWdodD0iMjIiIHZpZXdCb3g9IjAgMCA4NCAyMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTMgMC41SDc4Ljc1NzhDNzkuNDIwNyAwLjUwMDExOSA4MC4wNTY3IDAuNzYzNjg4IDgwLjUyNTQgMS4yMzI0Mkw4Mi43Njc2IDMuNDc0NjFDODMuMjM2MyAzLjk0MzM0IDgzLjQ5OTkgNC41NzkzMSA4My41IDUuMjQyMTlWMTguNUM4My41IDE5Ljg4MDcgODIuMzgwNyAyMSA4MSAyMUgzQzEuNjE5MjkgMjEgMC41IDE5Ljg4MDcgMC41IDE4LjVWM0MwLjUgMS42MTkyOSAxLjYxOTI5IDAuNSAzIDAuNVoiIGZpbGw9IiNFMUY1RjEiLz4KPHBhdGggZD0iTTMgMC41SDc4Ljc1NzhDNzkuNDIwNyAwLjUwMDExOSA4MC4wNTY3IDAuNzYzNjg4IDgwLjUyNTQgMS4yMzI0Mkw4Mi43Njc2IDMuNDc0NjFDODMuMjM2MyAzLjk0MzM0IDgzLjQ5OTkgNC41NzkzMSA4My41IDUuMjQyMTlWMTguNUM4My41IDE5Ljg4MDcgODIuMzgwNyAyMSA4MSAyMUgzQzEuNjE5MjkgMjEgMC41IDE5Ljg4MDcgMC41IDE4LjVWM0MwLjUgMS42MTkyOSAxLjYxOTI5IDAuNSAzIDAuNVoiIGZpbGw9IiNGRjZGMjAiLz4KPHBhdGggZD0iTTMgMC41SDc4Ljc1NzhDNzkuNDIwNyAwLjUwMDExOSA4MC4wNTY3IDAuNzYzNjg4IDgwLjUyNTQgMS4yMzI0Mkw4Mi43Njc2IDMuNDc0NjFDODMuMjM2MyAzLjk0MzM0IDgzLjQ5OTkgNC41NzkzMSA4My41IDUuMjQyMTlWMTguNUM4My41IDE5Ljg4MDcgODIuMzgwNyAyMSA4MSAyMUgzQzEuNjE5MjkgMjEgMC41IDE5Ljg4MDcgMC41IDE4LjVWM0MwLjUgMS42MTkyOSAxLjYxOTI5IDAuNSAzIDAuNVoiIHN0cm9rZT0iI0ZGNkYyMCIvPgo8L3N2Zz4K');
  background-repeat: no-repeat;
  background-position: top right;
  -webkit-background-size: cover;
  background-size: cover;
}

.profi-pst-style {
  background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDYiIGhlaWdodD0iMjIiIHZpZXdCb3g9IjAgMCA0NiAyMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTMgMC41SDQwLjQ1NTFDNDEuMDc4OSAwLjUgNDEuNjgxIDAuNzMyNzI3IDQyLjE0MTYgMS4xNTMzMkw0NC42ODU1IDMuNDc2NTZDNDUuMjA0MyAzLjk1MDIyIDQ1LjUgNC42MjA3NiA0NS41IDUuMzIzMjRWMTguNUM0NS41IDE5Ljg4MDcgNDQuMzgwNyAyMSA0MyAyMUgzQzEuNjE5MjkgMjEgMC41IDE5Ljg4MDcgMC41IDE4LjVWM0MwLjUgMS42MTkyOSAxLjYxOTI5IDAuNSAzIDAuNVoiIGZpbGw9IiNFMUY1RjEiLz4KPHBhdGggZD0iTTMgMC41SDQwLjQ1NTFDNDEuMDc4OSAwLjUgNDEuNjgxIDAuNzMyNzI3IDQyLjE0MTYgMS4xNTMzMkw0NC42ODU1IDMuNDc2NTZDNDUuMjA0MyAzLjk1MDIyIDQ1LjUgNC42MjA3NiA0NS41IDUuMzIzMjRWMTguNUM0NS41IDE5Ljg4MDcgNDQuMzgwNyAyMSA0MyAyMUgzQzEuNjE5MjkgMjEgMC41IDE5Ljg4MDcgMC41IDE4LjVWM0MwLjUgMS42MTkyOSAxLjYxOTI5IDAuNSAzIDAuNVoiIGZpbGw9IiNBNDRFRkYiLz4KPHBhdGggZD0iTTMgMC41SDQwLjQ1NTFDNDEuMDc4OSAwLjUgNDEuNjgxIDAuNzMyNzI3IDQyLjE0MTYgMS4xNTMzMkw0NC42ODU1IDMuNDc2NTZDNDUuMjA0MyAzLjk1MDIyIDQ1LjUgNC42MjA3NiA0NS41IDUuMzIzMjRWMTguNUM0NS41IDE5Ljg4MDcgNDQuMzgwNyAyMSA0MyAyMUgzQzEuNjE5MjkgMjEgMC41IDE5Ljg4MDcgMC41IDE4LjVWM0MwLjUgMS42MTkyOSAxLjYxOTI5IDAuNSAzIDAuNVoiIHN0cm9rZT0iI0E0NEVGRiIvPgo8L3N2Zz4K');
  background-repeat: no-repeat;
  background-position: top right;
  -webkit-background-size: cover;
  background-size: cover;
}

.has-canceled {
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid #FF3B30;
  border-radius: 3px;
  font-style: normal;
  font-weight: 500;
  font-size: 10px;
  line-height: 12px;
  color: #FF3B30;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 15px;
  margin-top: auto;
  margin-bottom: -9px;
}

.user-inactive {
  &:not(td.active) .table__cell-body {
    border-color: rgba(204, 204, 204, 0.5)!important;
  }

  .table__cell-body {
    position: relative;
    background: #fff!important;
    border: unset;
  }

  .table__cell-body::before {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0;
    opacity: 0.5;
    background: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOTQiIGhlaWdodD0iOTQiIHZpZXdCb3g9IjAgMCA5NCA5NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGxpbmUgeDE9Ii0wLjM1MzU1MyIgeTE9IjcuNTkyNTUiIHgyPSI2LjY0NjQ1IiB5Mj0iMC41OTI1NTMiIHN0cm9rZT0iI0NDQ0NDQyIvPgo8bGluZSB4MT0iLTAuMzUzNTUzIiB5MT0iMTMuNTkyNiIgeDI9IjEyLjY0NjQiIHkyPSIwLjU5MjU1MiIgc3Ryb2tlPSIjQ0NDQ0NDIi8+CjxsaW5lIHgxPSItMC4zNTM1NTMiIHkxPSIxOS41OTI2IiB4Mj0iMTguNjQ2NCIgeTI9IjAuNTkyNTUzIiBzdHJva2U9IiNDQ0NDQ0MiLz4KPGxpbmUgeDE9Ii0wLjM1MzU1MyIgeTE9IjI1LjU5MjYiIHgyPSIyNC42NDY0IiB5Mj0iMC41OTI1NTMiIHN0cm9rZT0iI0NDQ0NDQyIvPgo8bGluZSB4MT0iLTAuMzUzNTUzIiB5MT0iMzEuNTkyNiIgeDI9IjMwLjY0NjQiIHkyPSIwLjU5MjU1MiIgc3Ryb2tlPSIjQ0NDQ0NDIi8+CjxsaW5lIHgxPSItMC4zNTM1NTMiIHkxPSIzNy41OTI2IiB4Mj0iMzYuNjQ2NCIgeTI9IjAuNTkyNTU0IiBzdHJva2U9IiNDQ0NDQ0MiLz4KPGxpbmUgeDE9Ii0wLjM1MzU1MyIgeTE9IjQzLjU5MjYiIHgyPSI0Mi42NDY0IiB5Mj0iMC41OTI1NTMiIHN0cm9rZT0iI0NDQ0NDQyIvPgo8bGluZSB4MT0iLTAuMzUzNTUzIiB5MT0iNDkuNTkyNiIgeDI9IjQ4LjY0NjQiIHkyPSIwLjU5MjU1NSIgc3Ryb2tlPSIjQ0NDQ0NDIi8+CjxsaW5lIHgxPSItMC4xMTQ1NCIgeTE9IjU1LjM1MzUiIHgyPSI1NC44ODU1IiB5Mj0iMC4zNTM1NCIgc3Ryb2tlPSIjQ0NDQ0NDIi8+CjxsaW5lIHgxPSItMC4xMTQ1NCIgeTE9IjYxLjM1MzUiIHgyPSI2MC44ODU1IiB5Mj0iMC4zNTM1MzkiIHN0cm9rZT0iI0NDQ0NDQyIvPgo8bGluZSB4MT0iLTAuMTE0NTQiIHkxPSI2Ny4zNTM1IiB4Mj0iNjYuODg1NSIgeTI9IjAuMzUzNTM4IiBzdHJva2U9IiNDQ0NDQ0MiLz4KPGxpbmUgeDE9Ii0wLjExNDU0IiB5MT0iNzMuMzUzNSIgeDI9IjcyLjg4NTUiIHkyPSIwLjM1MzUzNyIgc3Ryb2tlPSIjQ0NDQ0NDIi8+CjxsaW5lIHgxPSItMC4xMTQ1NCIgeTE9Ijc5LjM1MzUiIHgyPSI3OC44ODU1IiB5Mj0iMC4zNTM1NDEiIHN0cm9rZT0iI0NDQ0NDQyIvPgo8bGluZSB4MT0iLTAuMTE0NTQiIHkxPSI4NS4zNTM1IiB4Mj0iODQuODg1NSIgeTI9IjAuMzUzNTQiIHN0cm9rZT0iI0NDQ0NDQyIvPgo8bGluZSB4MT0iLTAuMTE0NTQiIHkxPSI5MS4zNTM1IiB4Mj0iOTAuODg1NSIgeTI9IjAuMzUzNTM5IiBzdHJva2U9IiNDQ0NDQ0MiLz4KPGxpbmUgeDE9IjMuODg1NDYiIHkxPSI5My4zNTM1IiB4Mj0iOTIuODg1NSIgeTI9IjQuMzUzNTQiIHN0cm9rZT0iI0NDQ0NDQyIvPgo8bGluZSB4MT0iOS44ODU0NiIgeTE9IjkzLjM1MzUiIHgyPSI5Mi44ODU1IiB5Mj0iMTAuMzUzNSIgc3Ryb2tlPSIjQ0NDQ0NDIi8+CjxsaW5lIHgxPSIxNS44ODU1IiB5MT0iOTMuMzUzNSIgeDI9IjkyLjg4NTUiIHkyPSIxNi4zNTM1IiBzdHJva2U9IiNDQ0NDQ0MiLz4KPGxpbmUgeDE9IjIxLjg4NTUiIHkxPSI5My4zNTM1IiB4Mj0iOTIuODg1NSIgeTI9IjIyLjM1MzUiIHN0cm9rZT0iI0NDQ0NDQyIvPgo8bGluZSB4MT0iMjcuODg1NSIgeTE9IjkzLjM1MzUiIHgyPSI5Mi44ODU1IiB5Mj0iMjguMzUzNSIgc3Ryb2tlPSIjQ0NDQ0NDIi8+CjxsaW5lIHgxPSIzMy44ODU1IiB5MT0iOTMuMzUzNSIgeDI9IjkyLjg4NTUiIHkyPSIzNC4zNTM1IiBzdHJva2U9IiNDQ0NDQ0MiLz4KPGxpbmUgeDE9IjM5LjY0NjQiIHkxPSI5My41OTI2IiB4Mj0iOTIuNjQ2NCIgeTI9IjQwLjU5MjUiIHN0cm9rZT0iI0NDQ0NDQyIvPgo8bGluZSB4MT0iNDUuNjQ2NCIgeTE9IjkzLjU5MjYiIHgyPSI5Mi42NDY0IiB5Mj0iNDYuNTkyNiIgc3Ryb2tlPSIjQ0NDQ0NDIi8+CjxsaW5lIHgxPSI1MS42NDY0IiB5MT0iOTMuNTkyNiIgeDI9IjkyLjY0NjQiIHkyPSI1Mi41OTI2IiBzdHJva2U9IiNDQ0NDQ0MiLz4KPGxpbmUgeDE9IjU3LjY0NjQiIHkxPSI5My41OTI2IiB4Mj0iOTIuNjQ2NCIgeTI9IjU4LjU5MjYiIHN0cm9rZT0iI0NDQ0NDQyIvPgo8bGluZSB4MT0iNjMuNjQ2NCIgeTE9IjkzLjU5MjYiIHgyPSI5Mi42NDY0IiB5Mj0iNjQuNTkyNiIgc3Ryb2tlPSIjQ0NDQ0NDIi8+CjxsaW5lIHgxPSI2OS42NDY0IiB5MT0iOTMuNTkyNiIgeDI9IjkyLjY0NjQiIHkyPSI3MC41OTI2IiBzdHJva2U9IiNDQ0NDQ0MiLz4KPGxpbmUgeDE9Ijc1LjY0NjQiIHkxPSI5My41OTI2IiB4Mj0iOTIuNjQ2NCIgeTI9Ijc2LjU5MjYiIHN0cm9rZT0iI0NDQ0NDQyIvPgo8bGluZSB4MT0iODEuNjQ2NCIgeTE9IjkzLjU5MjYiIHgyPSI5Mi42NDY0IiB5Mj0iODIuNTkyNiIgc3Ryb2tlPSIjQ0NDQ0NDIi8+CjxsaW5lIHgxPSI4OC42NDY0IiB5MT0iOTIuNTkyNiIgeDI9IjkyLjY0NjQiIHkyPSI4OC41OTI2IiBzdHJva2U9IiNDQ0NDQ0MiLz4KPC9zdmc+Cg==')!important;
  }

  .table__cell-vacation {
    display: none;
  }

  .has-canceled {
    opacity: .5;
  }
}
</style>